import { Injectable, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Device } from '@capacitor/device';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { CryptoService } from '../../services/crypto.service';
import { NetworkService } from '../../services/network.service';
import { SignService } from '../../services/sign.service';
import { SignatureStorageService } from '../../services/signature-storage.service';
import type { SignAffidavit } from '../generated/models/sign-affidavit';
import type { ESignature } from '../generated/models/e-signature';
import type {
  AffidavitRecord,
  EsignCreateResponse,
  EsignqueueResponse,
  SignatureData,
  SignJob,
  SignJobStatus,
} from '../../models/signature.model';
import { extractStatusMessage } from './adapter.utils';

// ─── SignAdapter ─────────────────────────────────────────────────────────────

/** Raw shape returned by `GET /process-server/{id}/sign-queue`. */
interface RawSignJob {
  id: string | number;
  name?: string | null;
  signing_status: string;
  rejection_reason?: string | null;
  signed_at?: string | null;
  signed_lat?: number | string | null;
  signed_lng?: number | string | null;
}

/**
 * Maps API `signing_status` values to the domain {@link SignJobStatus} enum.
 * Both `'ready'` and `'approved_for_signing'` resolve to `'ready'`.
 * Both `'signed'` and `'approved'` resolve to `'signed'`.
 */
const STATUS_MAP: Readonly<Record<string, SignJobStatus>> = {
  ready:                'ready',
  approved_for_signing: 'ready',
  pending_review:       'review',
  signed:               'signed',
  approved:             'signed',
  rejected:             'rejected',
};

/**
 * Adapter for `GET /process-server/{serverId}/sign-queue`.
 *
 * @remarks
 * `loadSignQueue()` fetches the sign queue and populates `SignService.signQueue`
 * via `setSignQueue()`.  `toSignJob()` is exposed as public for unit testing.
 *
 * @example
 * ```ts
 * this.signAdapter.loadSignQueue().subscribe();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SignAdapter {
  private readonly http             = inject(HttpClient);
  private readonly authService      = inject(AuthService);
  private readonly cryptoService    = inject(CryptoService);
  private readonly networkService   = inject(NetworkService);
  private readonly signService      = inject(SignService);
  private readonly signatureStorage = inject(SignatureStorageService);

  /**
   * Resets the in-memory signature whenever there is no signed-in user, so it
   * never carries over to the next person who signs in on the same tab.
   *
   * @remarks
   * Whether the PERSISTED signature is also removed is decided by
   * `AuthService.clearAllStorage()` (cleared only when the user has no
   * unsent submissions / event logs — 2026-10-09). If it was kept,
   * {@link restoreLocalSignature} reloads it on the same user's next sign-in.
   */
  constructor() {
    effect(() => {
      if (!this.authService.token()) this.signService.clearSignature();
    });
  }

  /**
   * Restores the locally-cached signature from `SecureStorage` into
   * `SignService.savedSignature` without hitting the network.
   * Call this before `loadSavedSignature()` for instant display on page load.
   */
  async restoreLocalSignature(): Promise<void> {
    const token = this.authService.token();
    if (!token) return;
    const value = await this.signatureStorage.get(token.user.entityId);
    if (value) this.signService.saveSignature(value);
  }

  /**
   * Fetches the saved process-server signature from
   * `GET /process-server/{serverId}/esignature` and populates
   * `SignService.savedSignature` when `esign_data` is present.
   *
   * @remarks
   * When `esign_securekey` and `esign_secureiv` are present the data is
   * decrypted via `CryptoService.decryptRawData` before saving. If decryption
   * throws (malformed key/IV) the raw `esign_data` is used as a fallback.
   * When the keys are absent (unencrypted dev data) `esign_data` is used
   * directly — a `data:image/png;base64,` prefix is prepended when missing.
   * - If the API returns an empty / absent `esign_data`, `savedSignature` is
   *   left untouched so the canvas capture UI remains visible.
   * - HTTP errors are swallowed — callers do not need their own `catchError`.
   * - Returns `of(undefined)` immediately when no auth token is available.
   */
  loadSavedSignature(): Observable<void> {
    const token = this.authService.token();
    if (!token) return of(undefined);
    const serverId = token.user.entityId;
    return this.http
      .get<unknown>(
        `${environment.apiUrl}/process-server/${serverId}/esignature`,
      )
      .pipe(
        map(raw => {
          const sig = eSignatureToModel(raw);
          if (!sig.data) return;

          let dataUrl: string;
          if (sig.secureKey && sig.secureIv) {
            try {
              const decrypted = this.cryptoService.decryptRawData(
                sig.secureKey, sig.secureIv, sig.data,
              );
              dataUrl = decrypted.startsWith('data:')
                ? decrypted
                : `data:image/png;base64,${decrypted}`;
            } catch {
              // Decryption failed — fall back to using raw data with prefix
              dataUrl = sig.data.startsWith('data:')
                ? sig.data
                : `data:image/png;base64,${sig.data}`;
            }
          } else {
            dataUrl = sig.data.startsWith('data:')
              ? sig.data
              : `data:image/png;base64,${sig.data}`;
          }

          this.signService.saveSignature(dataUrl);
          void this.signatureStorage.save(serverId, dataUrl);
        }),
        catchError(() => of(undefined)),
      );
  }

  /**
   * Submits signature — online waits for API, offline saves locally.
   *
   * @remarks
   * **Online:** Save to storage + wait for API upload success
   * **Offline:** Save to storage only, return immediately
   *
   * When online: returns observable that completes when API responds.
   * When offline: returns observable that completes immediately after local save.
   *
   * Component shows "Signature saved" toast when observable completes.
   *
   * @param dataUrl - Base64 PNG data URL from the signature canvas.
   */
  submitSignature(dataUrl: string): Observable<void> {
    const token = this.authService.token();
    if (!token) return of(undefined);
    const serverId = token.user.entityId;

    // Always save to local storage immediately
    void this.signatureStorage.save(serverId, dataUrl);

    // If online: wait for API upload success before returning
    if (this.networkService.isOnline()) {
      return this.saveEncryptedSignature(dataUrl);
    }

    // If offline: return immediately (already saved locally)
    return of(undefined);
  }

  /**
   * Encrypts the given signature data URL and POSTs it to
   * `POST /process-server/{serverId}/esignature`.
   *
   * @remarks
   * Collects `esign_deviceid` and `esign_devicetype` from `@capacitor/device`
   * (required fields by the API) via `Promise.allSettled` so a permission
   * denial on either call does not abort the save.
   * Uses `CryptoService.generateSecureKeyAndIV()` + `encryptRawData()` to
   * encrypt the data URL before transmission.
   * - HTTP errors are swallowed — callers do not need their own `catchError`.
   * - Returns `of(undefined)` immediately when no auth token is available.
   *
   * @param dataUrl - Base64 PNG data URL from the signature canvas.
   */
  saveEncryptedSignature(dataUrl: string): Observable<void> {
    const token = this.authService.token();
    if (!token) return of(undefined);
    const serverId = token.user.entityId;

    const { secureKey, secureIV, encsecureKey, encsecureIV } =
      this.cryptoService.generateSecureKeyAndIV();
    const encryptedData = this.cryptoService.encryptRawData(secureKey, secureIV, dataUrl);

    return from(Promise.allSettled([Device.getId(), Device.getInfo()])).pipe(
      switchMap(([idResult, infoResult]) => {
        const deviceId   = idResult.status   === 'fulfilled' ? idResult.value.identifier              : '';
        const deviceType = infoResult.status === 'fulfilled' ? infoResult.value.operatingSystem ?? '' : '';

        const payload = eSignatureToPayload({
          data:       encryptedData,
          secureKey:  encsecureKey,
          secureIv:   encsecureIV,
          platform:   deviceType,
          deviceId,
          deviceType,
        });

        return this.http.post<unknown>(
          `${environment.apiUrl}/process-server/${serverId}/esignature`,
          payload,
        );
      }),
      map(() => undefined as void),
      tap(() => {
        // Persist plain data URL to local storage after confirmed POST
        void this.signatureStorage.save(serverId, dataUrl);
      }),
      catchError(() => of(undefined)),
    );
  }

  /**
   * Fetches the sign queue and populates `SignService.signQueue`.
   * Returns an `Observable<void>` that always completes — HTTP errors are
   * swallowed so callers do not need their own `catchError`.
   * Returns `of(undefined)` immediately when no auth token is available.
   */
  loadSignQueue(): Observable<void> {
    const token = this.authService.token();
    if (!token) return of(undefined);
    const serverId = token.user.entityId;
    return this.http
      .get<{ data: RawSignJob[] }>(
        `${environment.apiUrl}/process-server/${serverId}/sign-queue`,
      )
      .pipe(
        map(res => {
          const jobs = (res?.data ?? []).map(r => this.toSignJob(r));
          this.signService.setSignQueue(jobs);
        }),
        catchError(() => of(undefined)),
      );
  }

  /**
   * Maps a single raw API sign-queue entry to the domain {@link SignJob}.
   *
   * @param raw - Raw JSON object from the API response `data[]` array.
   */
  toSignJob(raw: RawSignJob): SignJob {
    const apiStatus = String(raw.signing_status ?? '');
    const status = STATUS_MAP[apiStatus] ?? this.warnUnexpected(apiStatus);
    const lat = Number(raw.signed_lat);
    const lng = Number(raw.signed_lng);
    const hasCoords =
      raw.signed_lat != null &&
      raw.signed_lng != null &&
      !isNaN(lat) &&
      !isNaN(lng);
    return {
      id:              String(raw.id),
      name:            raw.name ?? '',
      status,
      rejectionReason: status === 'rejected' ? (raw.rejection_reason ?? null) : null,
      signedAt:        raw.signed_at ?? undefined,
      signedCoords:    hasCoords ? { latitude: lat, longitude: lng } : null,
    };
  }

  /**
   * Logs an unexpected `signing_status` value and returns the safe default `'review'`.
   *
   * @param value - The unrecognised status string from the API.
   */
  private warnUnexpected(value: string): SignJobStatus {
    console.warn(`[SignAdapter] Unexpected signing_status: "${value}" — defaulting to 'review'`);
    return 'review';
  }
}

/**
 * Maps the `POST /process-server/{serverId}/jobs/esignqueue` response.
 *
 * @remarks
 * Live API returns `{ "status": "success", "message": "Affidavit Document Signed & Approved successfully!" }`.
 *
 * @param raw - Untyped API response.
 */
export function esignqueueToModel(raw: unknown): EsignqueueResponse {
  return extractStatusMessage(raw);
}

/**
 * Builds the request payload for
 * `POST /process-server/{serverId}/jobs/esignqueue`.
 *
 * @param records - Signed or rejected affidavit records from the UI.
 */
export function affidavitToPayload(records: AffidavitRecord[]): SignAffidavit {
  return {
    signed_affidavits: records.map((r) => ({
      job_number: r.jobNumber,
      affidavit_sign_status: r.signStatus,
      affidavit_sign_key: r.signKey,
      affidavit_sign_iv: r.signIv,
      affidavit_sign_platform: r.platform,
      affidavit_sign_document: r.document,
      affidavit_sign_datetime: r.signedAt,
      affidavit_rejected_comments: r.rejectedComments,
      affidavit_rejected_datetime: r.rejectedAt,
    })),
  };
}

/**
 * Maps the `POST /process-server/{serverId}/esignature` create response.
 *
 * @remarks
 * Live API returns `{ "status": "success", "message": "E-sign created successfully!" }`.
 *
 * @param raw - Untyped API response.
 */
export function eSignCreateToModel(raw: unknown): EsignCreateResponse {
  return extractStatusMessage(raw);
}

/**
 * Maps a domain {@link SignatureData} to the `ESignature` API shape for
 * `POST /process-server/{serverId}/esignature`.
 *
 * @param sig - Signature captured on device.
 */
export function eSignatureToPayload(sig: SignatureData): ESignature {
  return {
    esign_crptodata: sig.cryptoData,
    esign_data: sig.data,
    esign_deviceid: sig.deviceId,
    esign_devicetype: sig.deviceType,
    esign_platform: sig.platform,
    esign_secureiv: sig.secureIv,
    esign_securekey: sig.secureKey,
  };
}

/**
 * Maps the raw `GET /process-server/{serverId}/esignature` response to
 * the domain {@link SignatureData}.
 *
 * @remarks
 * The GET endpoint wraps the signature in a `{ data: {...} }` envelope and
 * adds an `esigner_id` field. This function unwraps that envelope.
 * Also accepts a bare `ESignature` object (e.g. for round-trip testing).
 *
 * @param raw - Raw API response (envelope or bare ESignature object).
 */
export function eSignatureToModel(raw: unknown): SignatureData {
  const envelope = raw as { data?: Record<string, unknown> };
  const obj: Record<string, unknown> =
    envelope.data !== null && typeof envelope.data === 'object'
      ? (envelope.data as Record<string, unknown>)
      : (raw as Record<string, unknown>);

  const signerId = obj['esigner_id'] as number | undefined;
  return {
    ...(signerId !== undefined && { signerId }),
    cryptoData: obj['esign_crptodata'] as string | undefined,
    data: obj['esign_data'] as string | undefined,
    deviceId: obj['esign_deviceid'] as string | undefined,
    deviceType: obj['esign_devicetype'] as string | undefined,
    platform: obj['esign_platform'] as string | undefined,
    secureIv: obj['esign_secureiv'] as string | undefined,
    secureKey: obj['esign_securekey'] as string | undefined,
  };
}
